import React from 'react'

/**
 * PeriodPickerModal - shown when a student commits to a dual-period course
 * (Programme.periods_available includes both "laboral" and "pos_laboral").
 *
 * The platform doesn't model fees - the student already knows those from the
 * university. What we capture is which admission cohort they're preparing
 * for: vagas pools, cutoffs, and schedule differ even though the exam
 * content is identical.
 */

export type Period = 'laboral' | 'pos_laboral' | 'ead'

interface Props {
  programmeName: string
  universityShortName: string
  priorityLabel: '1ª opção' | '2ª opção'
  periodsAvailable: string[]                                 // e.g. ["laboral","pos_laboral"] or ["laboral","pos_laboral","ead"]
  seats?: { laboral?: number; pos_laboral?: number; ead?: number }
  onPick:  (period: Period) => void
  onClose: () => void
}

const PERIOD_META: Record<Period, { icon: string; label: string; hint: string }> = {
  laboral:     { icon: '☀️', label: 'Laboral',     hint: 'Horário diurno'      },
  pos_laboral: { icon: '🌙', label: 'Pós-Laboral', hint: 'Horário nocturno'    },
  ead:         { icon: '🖥️', label: 'EaD',         hint: 'Ensino a Distância'  },
}

export function PeriodPickerModal({ programmeName, universityShortName, priorityLabel, periodsAvailable, seats, onPick, onClose }: Props) {
  // Narrow to the known period keys we render, preserving edital order.
  const known = periodsAvailable.filter((p): p is Period => p === 'laboral' || p === 'pos_laboral' || p === 'ead')

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">Escolhe o período</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {programmeName} · {universityShortName} · <span className="text-indigo-600 font-semibold">{priorityLabel}</span>
            </p>
            <p className="text-[11px] text-gray-400 mt-1">
              O exame é igual, mas as vagas e o ranking são calculados em pools separados.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-300 hover:text-gray-500 text-2xl leading-none">×</button>
        </div>

        <div className={`p-5 grid gap-3 ${known.length >= 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {known.map(period => {
            const meta = PERIOD_META[period]
            const vagas = seats?.[period]
            return (
              <button
                key={period}
                onClick={() => onPick(period)}
                className="border-2 border-gray-200 hover:border-indigo-400 hover:bg-indigo-50/40 rounded-xl p-4 text-left transition-all"
              >
                <div className="text-2xl mb-1">{meta.icon}</div>
                <p className="font-semibold text-gray-900 text-sm">{meta.label}</p>
                <p className="text-xs text-gray-500">{meta.hint}</p>
                {vagas !== undefined && (
                  <p className="text-[10px] text-indigo-600 font-semibold mt-1">{vagas} vagas</p>
                )}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}
